import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground shadow',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        destructive: 'border-transparent bg-destructive text-destructive-foreground shadow',
        outline: 'text-foreground',
        critical: 'border-transparent bg-gradient-to-r from-red-600 to-red-500 text-white',
        high: 'border-transparent bg-gradient-to-r from-sabio-yellow to-sabio-yellow-dark text-sabio-navy',
        medium: 'border-transparent bg-gradient-to-r from-sabio-purple to-indigo-500 text-white',
        low: 'border-transparent bg-gradient-to-r from-green-500 to-emerald-500 text-white',
        // Workflow statuses
        draft: 'border-transparent bg-gray-500/20 text-gray-600 dark:text-gray-400',
        review: 'border-transparent bg-yellow-500/20 text-yellow-700 dark:text-yellow-400',
        approved: 'border-transparent bg-blue-500/20 text-blue-700 dark:text-blue-400',
        published: 'border-transparent bg-green-500/20 text-green-700 dark:text-green-400',
        archived: 'border-transparent bg-gray-700/20 text-gray-500 dark:text-gray-500',
        // Subtle variants
        'subtle-purple': 'border-sabio-purple/20 bg-sabio-purple/10 text-sabio-purple dark:text-sabio-purple-light',
        'subtle-yellow': 'border-sabio-yellow/20 bg-sabio-yellow/10 text-sabio-yellow-dark dark:text-sabio-yellow',
        'subtle-green': 'border-green-500/20 bg-green-500/10 text-green-700 dark:text-green-400',
        'subtle-red': 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-400',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
